/* eslint-disable operator-linebreak */
import { useEffect, useState, useReducer, useRef } from 'react';
import { Form, Tooltip } from 'antd';
import { useRouteMatch, useHistory } from 'react-router-dom';

import moment from 'moment';

import { Button } from '../../components/Button';
import { FormSkeleton } from '../../components/FormSkeleton';
import { FormContainer } from '../../components/FormContainer';
import { PermissionCheck } from '../../components/PermissionCheck';
import { SettingsLayout } from './SettingsLayout';

import { Slug } from '../../types';
import SchedulerForm from './SchedulerForm'; // Adjust the path as needed
import { GET_CLINICS } from '../../graphql/query/constants';
import { useQueryClinics } from '../../graphql/query/getClinics';
import { useMutationUpdateHospital } from '../../graphql/mutation/updateHospital';
import { useQueryDashboardCaseMix } from '../../rest/query/useQueryDashboardCaseMix';
import { useUserContext } from '../../context';
import { HospitalSchedule, PeriodSchedule, WeeklySchedule } from './ScheduleTypes';
import { ACTION_TYPES, scheduleReducer } from './scheduleState/ScheduleReducer';
import { ScheduleContext } from './scheduleState/ScheduleContext';
import { findOverlappingPeriods } from './helpers';
import PeriodSchedulePreview from './PeriodSchedulePreview';
import { FormLabel } from '../../components/FormLabel';
import { RESTClient } from '../../rest/client';
import { adminApi } from '../../api/admin';
import { toast } from 'react-toastify';

export const BlockSchedule = () => {
  const [currentPeriodId, setCurrentPeriodId] = useState<string | null>(null);
  const [newPeriodAdded, setNewPeriodAdded] = useState(false);

  const {
    params: { refId },
  } = useRouteMatch<Slug>();
  const { data: userData } = useUserContext();
  const { data: clinics, loading } = useQueryClinics();

  const availableData = userData?.me.current?.organization?.availableData;

  const { data: caseDataCurrent, loading: caseDataCurrentLoading } = useQueryDashboardCaseMix({
    from: availableData?.from ?? '',
    to: availableData?.to ?? '',
  });

  const ORsList = [
    ...(caseDataCurrent?.metadata?.ORs.map((or) => ({
      label: or,
      value: or,
    })) || []),
    // { value: 'OR0005', label: 'OR0005' },
    // { value: 'OR0006', label: 'OR0006' },
    // { value: 'OR0007', label: 'OR0007' },
    // { value: 'OR0008', label: 'OR0008' },
  ];

  interface ClinicData {
    settings?: {
      hospital_schedule?: HospitalSchedule;
    };
  }

  const clinicData = clinics?.organizations.nodes.find(
    (clinic) => clinic.refId === refId,
  ) as ClinicData;

  const clinicSlug = clinics?.organizations.nodes?.find((clinic) => clinic.refId === refId)?.slug;

  const hasData = !!availableData?.from && !!availableData?.to;
  const [hospitalSchedule, hospitalDispatch] = useReducer(scheduleReducer, [] as HospitalSchedule);

  const { mutation, loading: mutationLoading } = useMutationUpdateHospital({});
  const [saveButtonText, setSaveButtonText] = useState('Save');
  const [scheduleError, setScheduleError] = useState('');
  const [activePeriodIdEditing, setActivePeriodIdEditing] = useState<string | null>(null);
  const [buildUtilTriggered, setBuildUtilTriggered] = useState(false);
  const [clearAllLoading, setClearAllLoading] = useState(false);
  const refreshDelayRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const history = useHistory();
  const cancel = () => {
    const clinicRefId = localStorage.getItem('hospital-refId');
    // history.push(`/${clinicRefId}/settings`);
    window.location.href = `/${clinicRefId}/settings`;
  };

  const isValidCurrentPeriodId = () => {
    const periodIds = hospitalSchedule?.map((p: PeriodSchedule) => p.id);
    return periodIds.includes(currentPeriodId);
  };

  // This is to trigger a re-render when the schedule is modified, beyond it's initial load (count > 2)
  // which enables the Cancel/Save buttons. Beyond 3 renders, no need to keep track of changes
  const [scheduleModified, setScheduleModified] = useState<number>(0);
  useEffect(() => {
    if (scheduleModified < 3) {
      setScheduleModified((s) => s + 1);
    }
  }, [hospitalSchedule]);

  useEffect(() => {
    return () => {
      if (refreshDelayRef.current) {
        clearTimeout(refreshDelayRef.current);
      }
    };
  }, []);

  const triggerBuildUtilization = async () => {
    if (buildUtilTriggered) return;
    setBuildUtilTriggered(true);

    try {
      await RESTClient.get(`/webhook/build-utilization-and-capacity?hospital_code=${refId}`);
    } finally {
      if (refreshDelayRef.current) {
        clearTimeout(refreshDelayRef.current);
      }
      refreshDelayRef.current = setTimeout(() => {
        setBuildUtilTriggered(false);
        refreshDelayRef.current = null;
      }, 30000);
    }
  };

  const handleClearAllSchedules = async () => {
    if (clearAllLoading) return;

    const confirmed = window.confirm(
      `Are you sure you want to delete ALL schedules for hospital ${refId}? This action cannot be undone.`,
    );

    if (!confirmed) return;

    setClearAllLoading(true);
    try {
      const response = await adminApi.deleteAllSchedules(refId);
      toast.success(response.data.message);

      // Refresh the page to show updated data
      setTimeout(() => {
        window.location.reload();
      }, 1000);
    } catch (error: any) {
      // eslint-disable-next-line no-console
      console.error('Failed to delete schedules:', error);
      toast.error(error.response?.data?.message || 'Failed to delete schedules');
    } finally {
      setClearAllLoading(false);
    }
  };
  // If a new period is added, set the active period to the new one
  useEffect(() => {
    if (newPeriodAdded) {
      const newPriodId = hospitalSchedule[hospitalSchedule.length - 1].id;
      setCurrentPeriodId(newPriodId);
      setNewPeriodAdded(false);
      setActivePeriodIdEditing(newPriodId);
    }
  }, [newPeriodAdded]);

  useEffect(() => {
    // setActivePeriodIdEditing(null);
  }, [currentPeriodId]);

  useEffect(() => {
    hospitalDispatch({
      type: ACTION_TYPES.LOAD_HOLIDAYS,
      payload: {},
    });
  }, []);

  useEffect(() => {
    const periodDates = hospitalSchedule.map((s: { [x: string]: string }) => {
      return { name: s['name'], startDate: s['startDate'], endDate: s['endDate'] };
    });
    const overlappedPeriods = findOverlappingPeriods(periodDates);

    if (overlappedPeriods.length > 0) {
      setScheduleError(
        `Error: Overlapping periods found: ${overlappedPeriods
          .map((period) => `"${period[0]}" and "${period[1]}"`)
          .join(', ')}`,
      );
    } else {
      setScheduleError('');
    }
  }, [hospitalSchedule]);

  useEffect(() => {
    if (clinicData?.settings?.hospital_schedule as HospitalSchedule) {
      const schedule = clinicData?.settings?.hospital_schedule;
      hospitalDispatch({
        type: ACTION_TYPES.INITIALIZE_SCHEDULE,
        payload: schedule,
      });
    }
  }, [clinicData?.settings]);

  const [form] = Form.useForm();

  const saveScheduleChanges = () => {
    setSaveButtonText('Saving ...');
    form.submit();
    setTimeout(() => {
      setSaveButtonText('Schedule Saved! ✓');
    }, 1000);
    setTimeout(() => {
      setSaveButtonText('Save');
      setScheduleModified(2);
    }, 2500);
  };

  const cancelScheduleChanges = () => {
    // setSaveButtonText('Saving ...');
    setTimeout(() => {
      setScheduleModified(0);
    }, 500);
  };

  if (loading) {
    return <FormSkeleton />;
  }

  // console.log('hospitalSchedule', hospitalSchedule);

  return (
    <SettingsLayout>
      <PermissionCheck check="updateOrganizations">
        <ScheduleContext.Provider value={{ hospitalSchedule, hospitalDispatch }}>
          {!hasData && (
            <div className="mb-4 p-4 bg-red-50 border-1 border-red-400 rounded-md">
              <p className="text-red-600">
                This hospital has no data yet. In order to create a schedule, you first need to have
                data synced for this hospital.
              </p>
            </div>
          )}
          <Form
            form={form}
            onFinish={(data) => {
              mutation({
                variables: {
                  ...data,
                  refId,
                  slug: clinicSlug,
                  settings: { hospital_schedule: hospitalSchedule },
                },
                refetchQueries: [GET_CLINICS],
              });
            }}
          >
            <FormContainer
              actions={
                scheduleModified > 2 && hasData ? (
                  <>
                    {scheduleError && scheduleError.length > 0 && (
                      <div className="block w-fit border-1 rounded-md p-2 pl-4 mr-auto border-red-400 text-red-600">
                        {scheduleError}
                      </div>
                    )}

                    <Button type="light" loading={loading} onClick={cancel}>
                      Cancel
                    </Button>
                    <Button
                      type={`${
                        saveButtonText === 'Save' && scheduleError.length == 0
                          ? 'primary'
                          : 'disabled'
                      }`}
                      loading={loading}
                      onClick={() => {
                        saveScheduleChanges();
                      }}
                    >
                      {saveButtonText}
                    </Button>
                  </>
                ) : (
                  <></>
                )
              }
              error={scheduleError}
            >
              <div className="mb-3 -mt-10 flex items-center">
                <div className="text-gray-600 flex-col">
                  <span>
                    Set your OR Prime Time &nbsp;
                    <Tooltip
                      overlayClassName="Tooltip-radius-sm"
                      title="These times will be used to calculate Utilization, FCOTS, LCOTE, and Capacity Calculator stats."
                    >
                      <span className="border-gray-400">ⓘ</span>
                    </Tooltip>
                  </span>
                </div>
                <div className="w-full flex flex-col justify-start items-center py-2 border-gray-200 whitespace-nowrap sm:w-[200px] 2xl:w-[300px] flex-shrink-0 ml-auto">
                  <FormLabel className="flex items-center font-bold text-xl bg-gray-200 p-1 my-1 pl-3 rounded-md w-full ml-0 h-16">
                    Grace Period (minutes)
                    <input
                      className="border-none focus:ring-0 sm:w-32 xl:w-18 text-xl h-12 ml-auto mr-2"
                      type="number"
                      value={hospitalSchedule?.[0]?.gracePeriod || 0}
                      disabled={!hasData}
                      onChange={(e) => {
                        hospitalDispatch({
                          type: ACTION_TYPES.UPDATE_PERIOD_PROPERTY,
                          payload: {
                            periodId: 'general',
                            property: 'gracePeriod',
                            updatedValue: e.target.value,
                          },
                        });
                      }}
                    />
                  </FormLabel>
                </div>
              </div>

              <div id="BlockSchedulerContainer" className="w-full flex gap-3">
                <div className="sm:w-[200px] 2xl:w-[300px] flex-shrink-0">
                  {hospitalSchedule.map((periodSchedule: PeriodSchedule, idx: number) => (
                    <div
                      className={`group items-center pl-7 border-1 border-gray-200 transition-all duration-300 pt-4 mb-2 rounded-md 
                    ${currentPeriodId == periodSchedule.id ? 'bg-blue' : 'bg-gray-100'} ${
                        hasData
                          ? 'cursor-pointer hover:shadow-md hover:bg-[#558EFD]'
                          : 'opacity-50 cursor-not-allowed'
                      }`}
                      onClick={() => hasData && setCurrentPeriodId(periodSchedule.id)}
                      key={idx}
                    >
                      <div
                        className={`font-bold text-2xl h-10 ${
                          currentPeriodId == periodSchedule.id ? 'text-white' : 'text-gray-800'
                        } group-hover:text-white`}
                      >
                        {periodSchedule.name}
                      </div>
                      <div
                        className={`flex justify-start text-md ${
                          currentPeriodId == periodSchedule.id ? 'text-white' : 'text-gray-500'
                        } group-hover:text-white h-12 -mt-2`}
                      >
                        {moment(periodSchedule.startDate).format('MM/DD/YYYY')} -{' '}
                        {moment(periodSchedule.endDate).format('MM/DD/YYYY')}
                      </div>
                    </div>
                  ))}

                  <div
                    className={`flex h-16 items-center justify-center text-white transition-all duration-300 rounded-md font-bold text-2xl mt-4 ${
                      hasData
                        ? 'bg-persianBlue hover:bg-[#558EFD] cursor-pointer'
                        : 'bg-gray-400 cursor-not-allowed opacity-50'
                    }`}
                    onClick={() => {
                      if (hasData) {
                        hospitalDispatch({
                          type: 'ADD_NEW_PERIOD',
                          payload: {
                            ORsList: ORsList,
                            dates: availableData,
                          },
                        });
                        setNewPeriodAdded(true);
                      }
                    }}
                  >
                    Add Period
                  </div>
                </div>
                <div className="flex-grow w-full">
                  {currentPeriodId &&
                  hospitalSchedule &&
                  hospitalSchedule.length &&
                  isValidCurrentPeriodId() &&
                  hasData ? (
                    <>
                      {activePeriodIdEditing && activePeriodIdEditing == currentPeriodId && (
                        <SchedulerForm
                          periodId={currentPeriodId}
                          ORsList={ORsList}
                          setActivePeriodIdEditing={setActivePeriodIdEditing}
                        />
                      )}
                      {activePeriodIdEditing == null && (
                        <PeriodSchedulePreview
                          periodId={currentPeriodId}
                          periodSchedule={
                            hospitalSchedule.find(
                              (period: PeriodSchedule) => period.id === currentPeriodId,
                            ) as PeriodSchedule
                          }
                          setActivePeriodIdEditing={setActivePeriodIdEditing}
                        />
                      )}
                    </>
                  ) : (
                    <div className=" min-h-full flex justify-center items-center text-3xl text-gray-600 border-1 border-gray-150 bg-gray-50 rounded-lg">
                      Please select a period to see the schedule or add a new one
                    </div>
                  )}
                </div>
              </div>
            </FormContainer>
          </Form>
          <div className="mt-4 flex gap-3">
            {hasData && (
              <Button type="light" onClick={triggerBuildUtilization} loading={buildUtilTriggered}>
                {buildUtilTriggered ? 'Refreshing Utilization' : `Refresh Utilization`}
              </Button>
            )}
            {hasData && userData?.me?.email === 'ramtin.rajikermani@intusurg.com' && (
              <Button type="dark" onClick={handleClearAllSchedules} loading={clearAllLoading}>
                {clearAllLoading ? 'Clearing...' : 'Clear All'}
              </Button>
            )}
          </div>
        </ScheduleContext.Provider>
      </PermissionCheck>
    </SettingsLayout>
  );
};
